import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Fade
} from '@mui/material';
// Removed random icons for clean design
import { useTeam } from '../../../shared/context/teamContext';
import { useSubscription } from '../../../shared/context/subscriptionContext';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

// Import our new components
import UsageDashboard from './components/UsageDashboard';
import BillingHistory from './components/BillingHistory';
import UpgradeCards from './components/UpgradeCards';
import WalletEnhancementConfig from './components/WalletEnhancementConfig';
import MembersPage from './components/MembersPage';

// Minimal Black Theme Design System
const styles = {
  primarycolor: "#0f172a", // Pure Black
  secondarycolor: "#0f172a", // Pure Black
  backgroundColor: "#ffffff", // Pure white background
  cardBg: "white",
  textPrimary: "#0f172a", // Primary text
  textSecondary: "#666666", // Gray for secondary text
  borderColor: "#E5E7EB", // Light gray borders
  headingFont: { fontFamily: "'Raleway', sans-serif", fontWeight: 600 },
  bodyFont: { fontFamily: "'Raleway', sans-serif", fontWeight: 400 },
  cardShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  cardShadowHover: "0 4px 16px rgba(0, 0, 0, 0.1)"
};

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={true} timeout={300}>
          <Box>{children}</Box>
        </Fade>
      )}
    </div>
  );
}

// Real data will be fetched from API endpoints

// Plans will be fetched from API endpoint /api/billing/plans

// All data will be fetched from respective API endpoints

const NewBilling = () => {
  const { selectedTeam } = useTeam();
  const {
    refreshSubscription,
    loading: subscriptionLoading,
    plan: subscriptionPlan,
    status: subscriptionStatusValue,
    isActive: subscriptionIsActive,
    billingCycle: subscriptionBillingCycle,
    currentPeriodStart,
    currentPeriodEnd,
    stripeCustomerId: subscriptionCustomerId,
    stripeSubscriptionId: subscriptionSubscriptionId,
    hasCQIntelligence: subscriptionHasCQ,
    limits: subscriptionLimits,
    accessiblePages: subscriptionAccessiblePages,
    error: subscriptionError
  } = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();

  // Derive active section from URL (secondary nav drives this)
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const settingsSegment = pathSegments.length > 2 ? pathSegments[2] : 'usage';
  const sectionToIndex = { usage: 0, members: 1, config: 2, billing: 3, plans: 3, teamsSection: 1, pricing: 3 };
  const currentTab = sectionToIndex[settingsSegment] ?? 0;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  
  // Real data state
  const [usageData, setUsageData] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [portalLoading, setPortalLoading] = useState(false);

  // Handle billing cycle change
  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
  };

  // Get API base URL from environment variable
  const API_URL = process.env.REACT_APP_API_SERVER_URL || 'https://backend.cryptique.io';
  
  // Handle Stripe redirect success/cancel
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const success = queryParams.get('success');
    const canceled = queryParams.get('canceled');
    
    if (success === 'true') {
      console.log('✅ Stripe checkout successful! Refreshing subscription data...');
      // Refresh subscription context to update the app-wide subscription status
      if (selectedTeam?._id && refreshSubscription) {
        refreshSubscription(selectedTeam._id);
      }
      // Clear URL parameters
      window.history.replaceState({}, document.title, location.pathname);
    } else if (canceled === 'true') {
      console.log('❌ Stripe checkout canceled');
      // Clear URL parameters
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search, selectedTeam?._id, refreshSubscription]);

  const loadBillingData = useCallback(async () => {
    if (!selectedTeam?._id) {
      console.log('No selected team, skipping billing data load');
      return;
    }

    if (subscriptionLoading) {
      console.log('Subscription still syncing, delaying billing data load');
      return;
    }

    console.log('Loading billing data for team:', selectedTeam._id);
    console.log('Using API URL:', API_URL);
    setLoading(true);
    try {
      const teamId = selectedTeam._id;

      const [usageResponse, historyResponse] = await Promise.all([
        axios.get(`${API_URL}/api/billing/usage/${teamId}`),
        axios.get(`${API_URL}/api/billing/history/${teamId}`)
      ]);

      if (usageResponse.status === 200) {
        const responseData = usageResponse.data.data;
        const resources = responseData.resources || {};

        const cleanedUsageData = {
          smartContracts: resources.smartContracts?.usage ?? responseData.smartContracts ?? 0,
          teamMembers: resources.teamMembers?.usage ?? responseData.teamMembers ?? 0,
          customQueries: resources.customQueries?.usage ?? responseData.customQueries ?? 0,
          customDashboards: resources.customDashboards?.usage ?? responseData.customDashboards ?? 0,
          customEvents: resources.customEvents?.usage ?? responseData.customEvents ?? 0,
          websites: resources.websites?.usage ?? responseData.websites ?? 0,
          // Credit-based usage data (NEW)
          // For baseCredits and apiCalls: show actual usage (consumed), not remaining
          baseCredits: resources.baseCredits?.usage ?? 0,
          apiCalls: resources.apiCalls?.usage ?? 0,
          // For addonCredits: show remaining (no limit, so remaining is what matters)
          addonCredits: resources.addonCredits?.remaining ?? resources.addonCredits?.usage ?? 0
        };

        const limits = {
          smartContracts: resources.smartContracts?.limit ?? null,
          teamMembers: resources.teamMembers?.limit ?? null,
          customQueries: resources.customQueries?.limit ?? null,
          customDashboards: resources.customDashboards?.limit ?? null,
          customEvents: resources.customEvents?.limit ?? null,
          websites: resources.websites?.limit ?? null,
          // Credit limits (NEW)
          baseCredits: resources.baseCredits?.limit ?? 0,
          apiCalls: resources.apiCalls?.limit ?? 0,
          addonCredits: resources.addonCredits?.limit ?? null // null = unlimited
        };

        // Store credit metadata for reset dates
        const creditMetadata = {
          lastResetAt: responseData.lastResetAt || null,
          billingAnchorAt: responseData.billingAnchorAt || null
        };

        setUsageData({ ...cleanedUsageData, ...creditMetadata });
        setPlanData({ limits });
      }

      if (historyResponse.status === 200) {
        setInvoices(historyResponse.data.data.invoices || []);
      }
    } catch (err) {
      console.error('Error loading billing data:', err);
      setError('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [selectedTeam?._id, API_URL, subscriptionLoading]);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  useEffect(() => {
    if (selectedTeam?._id && refreshSubscription) {
      refreshSubscription(selectedTeam._id, { force: true });
    }
  }, [selectedTeam?._id, refreshSubscription]);

  useEffect(() => {
    setUsageData(null);
    setPlanData(null);
    setInvoices([]);
  }, [selectedTeam?._id]);

  useEffect(() => {
    if (subscriptionError) {
      setError(subscriptionError);
    }
  }, [subscriptionError]);

  useEffect(() => {
    if (subscriptionLoading) {
      return;
    }

    setSubscriptionData({
      plan: subscriptionPlan,
      status: subscriptionStatusValue,
      isActive: subscriptionIsActive,
      billingCycle: subscriptionBillingCycle,
      currentPeriodStart,
      currentPeriodEnd,
      stripeCustomerId: subscriptionCustomerId,
      stripeSubscriptionId: subscriptionSubscriptionId,
      hasCQIntelligence: subscriptionHasCQ,
      limits: subscriptionLimits || {},
      accessiblePages: subscriptionAccessiblePages || []
    });
  }, [
    subscriptionLoading,
    subscriptionPlan,
    subscriptionStatusValue,
    subscriptionIsActive,
    subscriptionBillingCycle,
    currentPeriodStart,
    currentPeriodEnd,
    subscriptionCustomerId,
    subscriptionSubscriptionId,
    subscriptionHasCQ,
    subscriptionLimits,
    subscriptionAccessiblePages
  ]);

  // Real API functions
  const refreshUsageData = async () => {
    if (!selectedTeam?._id) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/billing/usage/${selectedTeam._id}`);
      if (response.status === 200) {
        // Extract the resources from the response and convert to simple values
        const responseData = response.data.data;
        const resources = responseData.resources || {};
        
        // Create a cleaned up version of the usage data
        const cleanedUsageData = {
          smartContracts: resources.smartContracts?.usage ?? responseData.smartContracts ?? 0,
          teamMembers: resources.teamMembers?.usage ?? responseData.teamMembers ?? 0,
          customQueries: resources.customQueries?.usage ?? responseData.customQueries ?? 0,
          customDashboards: resources.customDashboards?.usage ?? responseData.customDashboards ?? 0,
          customEvents: resources.customEvents?.usage ?? responseData.customEvents ?? 0,
          websites: resources.websites?.usage ?? responseData.websites ?? 0,
          // Credit-based usage data (NEW)
          // For baseCredits and apiCalls: show actual usage (consumed), not remaining
          baseCredits: resources.baseCredits?.usage ?? 0,
          apiCalls: resources.apiCalls?.usage ?? 0,
          // For addonCredits: show remaining (no limit, so remaining is what matters)
          addonCredits: resources.addonCredits?.remaining ?? resources.addonCredits?.usage ?? 0
        };
        
        // Extract simple limits from resources
        const limits = {
          smartContracts: resources.smartContracts?.limit ?? null,
          teamMembers: resources.teamMembers?.limit ?? null,
          customQueries: resources.customQueries?.limit ?? null,
          customDashboards: resources.customDashboards?.limit ?? null,
          customEvents: resources.customEvents?.limit ?? null,
          websites: resources.websites?.limit ?? null,
          // Credit limits (NEW)
          baseCredits: resources.baseCredits?.limit ?? 0,
          apiCalls: resources.apiCalls?.limit ?? 0,
          addonCredits: resources.addonCredits?.limit ?? null // null = unlimited
        };
        
        // Store credit metadata for reset dates
        const creditMetadata = {
          lastResetAt: responseData.lastResetAt || null,
          billingAnchorAt: responseData.billingAnchorAt || null
        };
        
        setUsageData({ ...cleanedUsageData, ...creditMetadata });
        setPlanData({ limits });
        
        // Refresh subscription context to ensure consistency
        if (selectedTeam?._id && refreshSubscription) {
          await refreshSubscription(selectedTeam._id);
        }
      } else {
        throw new Error('Failed to refresh usage data');
      }
    } catch (err) {
      setError('Failed to refresh usage data');
      console.error('Error refreshing usage data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    if (!selectedTeam?._id) return;
    
    setLoading(true);
    try {
      
      const teamSeg = encodeURIComponent(selectedTeam.name || 'team');
      const response = await axios.post(`${API_URL}/api/billing/upgrade`, {
        teamId: selectedTeam._id,
        planType: plan.type,
        billingCycle,
        addons: [],
        successUrl: `${window.location.origin}/${teamSeg}/settings/billing?success=true`,
        cancelUrl: `${window.location.origin}/${teamSeg}/settings/billing?canceled=true`
      });

      if (response.status === 200) {
        // Log the checkout URL for debugging
        console.log('Checkout URL:', response.data.data.url);
        
        // Redirect to checkout (Stripe handles the publishable key internally)
        window.location.href = response.data.data.url;
      } else {
        throw new Error(response.data.message || 'Failed to create checkout session');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to upgrade plan');
      console.error('Error selecting plan:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildAuthConfig = () => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    return {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      withCredentials: true
    };
  };

  const resolveStripeCustomerId = () => {
    return (
      subscriptionData?.stripeCustomerId ||
      selectedTeam?.subscription?.stripeCustomerId ||
      selectedTeam?.stripeCustomerId ||
      null
    );
  };

  const handleManageSubscription = async () => {
    const customerId = resolveStripeCustomerId();

    if (!selectedTeam?._id || !customerId) {
      setError('Missing subscription details for billing portal access.');
      return;
    }

    setPortalLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/billing/portal`,
        {
          teamId: selectedTeam._id,
          stripeCustomerId: customerId,
          returnUrl: `${window.location.origin}/${encodeURIComponent(selectedTeam.name || 'team')}/settings/billing`
        },
        buildAuthConfig()
      );

      const portalUrl = response.data?.data?.url || response.data?.url;
      if (portalUrl) {
        window.location.href = portalUrl;
      } else {
        throw new Error('No billing portal URL returned');
      }
    } catch (err) {
      console.error('Error opening billing portal:', err);
      setError(err.response?.data?.message || err.message || 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: styles.backgroundColor,
        py: 4
      }}
    >
      <Container maxWidth="lg">
        {error && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <TabPanel value={currentTab} index={0}>
          <Box sx={{ py: 2 }}>
            <UsageDashboard
              usageData={usageData}
              planData={planData}
              subscriptionData={subscriptionData}
              selectedTeamId={selectedTeam?._id}
              onRefresh={refreshUsageData}
            />
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Box sx={{ py: 2 }}>
            <MembersPage />
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Box sx={{ py: 2 }}>
            <WalletEnhancementConfig selectedTeamId={selectedTeam?.id} />
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Box sx={{ py: 2 }}>
            <UpgradeCards
              currentPlan={subscriptionData?.plan ?? subscriptionPlan}
              billingCycle={billingCycle}
              onBillingCycleChange={handleBillingCycleChange}
              onSelectPlan={handleSelectPlan}
              loading={loading}
            />
            <BillingHistory
              invoices={invoices}
              loading={loading}
              subscription={subscriptionData}
              onManageSubscription={handleManageSubscription}
              manageLoading={portalLoading}
            />
          </Box>
        </TabPanel>
      </Container>

      {(loading || subscriptionLoading) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(248, 250, 252, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <CircularProgress size={48} sx={{ color: styles.secondaryColor }} />
        </Box>
      )}
    </Box>
  );
};

export default NewBilling;
